// === Projects and Milestones (ADR-0024, YAG-338 wave 1) ===
//
// The Project is the single initiative noun: an end-dated, milestoned,
// human-commissioned initiative with exactly one accountable person. Commitment
// dissolved into its two natures, so an outcome promise ("15 qualified opps by
// Sep 30") is an end-dated Goal on a Team's Number and is NOT a Project; a
// delivery promise ("ship SSO by end of Q3") is.
//
// Backing tables: projects, project_milestones, project_milestone_work_items
// (migration 0124). Every DB-enforced invariant is noted on the field it
// constrains.
/**
 * A Project's lifecycle. `draft` is the whole pre-commissioning stretch: the
 * brief is a draft until commissioned and an uncommissioned Project never
 * reaches a customer's tracker. An arrived end date moves a live Project to
 * `awaiting_recommission`, which returns it to the accountable person to
 * re-commission or kill.
 */
export const PROJECT_STATUSES = [
    "draft",
    "commissioned",
    "awaiting_recommission",
    "completed",
    "killed",
];
/**
 * "Held by a Team when its work sits inside one, otherwise held directly by a
 * person. No shared ownership, no third option." Holding is accountability;
 * assignment is execution.
 */
export const PROJECT_HOLDER_KINDS = ["team", "person"];
/**
 * "Only the first milestone decomposes into tickets; later milestones stay
 * sketched." A sketched milestone has a Team, a title and an end date but no
 * work items; decomposing it is a re-scope, not a re-commission.
 */
export const PROJECT_MILESTONE_DECOMPOSITIONS = ["sketched", "decomposed"];
/**
 * What the Goal-redirect cop decided an ask is. `outcome` means the ask reads as
 * a promise about a number, which belongs on a Team's Number as an end-dated
 * Goal. `ambiguous` is not a rejection, it is the normal state of an amorphous
 * ask before the scoping conversation has clarified it.
 *
 * `outcome` alone does not decide whether the gate refuses. Read it with
 * `ProjectAskConfidence`: only a HIGH-confidence outcome is refused.
 *
 * `constraint` is the cop's THIRD route (ADR-0027): a standing engineering
 * budget. Target-shaped, so the original two-route cop refused it, but it maps
 * to no Team's Number and it cannot be a Project of its own because
 * un-end-dated work is a Rhythm. It attaches to the Project it governs instead.
 * The route is context-dependent by design and never fires on a bare ask: with
 * no Project to attach to there is nothing for a constraint to govern, and
 * ADR-0027 is explicit that a standing workspace-level budget object is future
 * work this stepping-stone does not build.
 */
export const PROJECT_ASK_SHAPES = [
    "delivery",
    "outcome",
    "ambiguous",
    "constraint",
];
/**
 * How sure the cop is that an `outcome` verdict is really an outcome promise,
 * and therefore whether the commissioning gate REFUSES it or merely SAYS SO.
 *
 * A keyword classifier cannot separate "double revenue by Q4" from "improve the
 * CI pipeline by Q4" with one threshold, and every round of tuning traded one
 * false-positive class for another. So the refusal splits by confidence instead:
 *
 *   - `high`: an explicit target (numeric, percentage, multiplier, range, or
 *     written in words) applied to a metric, with nothing to deliver. Refused at
 *     the commissioning gate, because the redirect is genuinely helpful.
 *   - `low`: the co-presence shape, a metric with a verb acting on it and a
 *     deadline but NO stated target. ADVISORY only, never blocking: this shape is
 *     indistinguishable from a legitimate amorphous ask ("we want to grow MRR,
 *     help us work out how"), which is the fourth entry point ADR-0024 opens, and
 *     a wrong refusal blocks real work with a nonsense message. It must always be
 *     surfaced to the accountable person, never dropped: what ADR-0024 cannot
 *     tolerate is a smuggle passing SILENTLY, and an advisory shown at the moment
 *     of commissioning is a challenge.
 *   - `none`: no outcome promise found. Always the value on a `delivery` or
 *     `ambiguous` verdict.
 */
export const PROJECT_ASK_CONFIDENCES = ["high", "low", "none"];
/**
 * Bounds on what a Project may carry. Constraints are a JSONB array on a row
 * that the commissioning gate reads, so an unbounded list is an unbounded read
 * on a hot path, and a constraint nobody can hold in their head at the gate is
 * not doing the job the ADR gave it.
 */
export const PROJECT_CONSTRAINT_LIMITS = {
    maxConstraints: 20,
    maxTextLength: 300,
    maxMetricLength: 120,
    maxLimitLength: 120,
};
// === The push to a customer's tracker (ADR-0024 step 5, YAG-338 wave 4) ===
//
// "Pushing tickets is a second, separate confirm. Creation is idempotent on
// re-push, so edit-then-re-commission never duplicates." The mirror map is
// Project <-> Linear Project / Jira project, Milestone <-> Linear project
// milestone / Jira version, ticket <-> Issue, and one row below records one
// side of that map for one local subject.
/** The trackers YAGNI can mirror a Project into. */
export const PROJECT_TRACKER_PROVIDERS = ["linear", "jira"];
/** Which side of the mirror map a link row records. */
export const PROJECT_TRACKER_SUBJECT_KINDS = ["project", "milestone", "ticket"];
/** What one emission did at the provider. */
export const PROJECT_PUSH_OUTCOMES = ["created", "updated", "unchanged"];
/**
 * Why the provider would not take one subject. Reported, never receipted.
 *
 * `target_unresolved` is ours rather than theirs: we could not decide WHICH
 * Linear team or Jira project this Project mirrors into. It is a refusal and
 * not a crash because the fix is a person naming the target, not a retry.
 */
export const PROJECT_PUSH_FAILURE_REASONS = [
    "no_tracker_connected",
    "target_unresolved",
    "provider_refused",
    "provider_unavailable",
];
/**
 * `RunArtifact.kind` for one exchange of a Project scoping conversation.
 *
 * The conversation lives on the run/work-item substrate rather than in a store
 * of its own, the way the ticket-altitude clarify loop does: the run that asked
 * the question is the audit trail, `runs.artifacts` already carries tenancy
 * through the run, and a parallel table would have to re-derive both.
 */
export const PROJECT_SCOPING_TURN_ARTIFACT_KIND = "project_scoping_turn";
/**
 * Who said it. Only `human` turns are read for budget-shaped content: the agent
 * asks questions, and a question mentioning a ceiling is not a person promising
 * one.
 */
export const PROJECT_SCOPING_TURN_ROLES = ["agent", "human"];
/**
 * Bounds on a conversation. Turn text is a JSONB write into a hot `runs` row and
 * arrives from a person, so it is clamped; the turn CEILING exists because a
 * clarify loop that has asked twenty questions is not clarifying any more, and
 * the honest move at that point is to decompose what is known and let the human
 * red-pen the brief.
 */
export const PROJECT_SCOPING_LIMITS = {
    maxTurns: 40,
    maxTurnLength: 4_000,
    /** Below this many human turns the conversation may not decompose. */
    minHumanTurnsBeforeDecomposition: 1,
};
/** Why a Project has to go back to its accountable person. */
export const PROJECT_RECOMMISSION_REASONS = [
    "end_date_arrived",
    "second_team_needed",
];
// ─────────────────────────────────────────────────────────────────────────────
// YAGNI Code per-developer usage (YAG-398) — the Track 1 admin surface.
//
// ADR-0028: YAGNI Code is the metered developer product, and per-developer
// usage visibility is a FEATURE of that track. These shapes carry customer
// spend (`sell_millicents`, what the rate card charged) only — never our cost
// or margin, which stay internal. Teams (Track 2) have no meter and these
// types must never back a Teams surface.
// ─────────────────────────────────────────────────────────────────────────────
/** The four priced lanes on the Track 1 rate card, in canonical display order. */
export const YAGNI_CODE_LANES = [
    "efficient",
    "standard",
    "advanced",
    "peak",
];
//# sourceMappingURL=projects.js.map